'use client';
import { cn } from 'src/lib/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createNoise3D } from 'simplex-noise';

type WavyBackgroundProps = {
    children?: React.ReactNode;
    className?: string;
    containerClassName?: string;
    colors?: string[];
    waveWidth?: number;
    backgroundFill?: string;
    blur?: number;
    speed?: 'slow' | 'fast';
    waveOpacity?: number;
    waveCount?: number;
    waveAmplitude?: number;
    waveY?: number;
    [key: string]: any;
};

export const WavyBackground = ({
    children,
    className,
    containerClassName,
    colors,
    waveWidth,
    backgroundFill,
    blur = 10,
    speed = 'fast',
    waveOpacity = 0.5,
    waveCount = 5,
    waveAmplitude = 100,
    waveY = 0.5,
    ...props
}: WavyBackgroundProps) => {
    const noise = useRef(createNoise3D());
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number | null>(null);

    const colorKey = colors?.join('|') || '';
    const speedValue = speed === 'fast' ? 0.002 : 0.001;
    const waveColors = useMemo(
        () => (colorKey ? colorKey.split('|') : ['#38bdf8', '#818cf8', '#c084fc', '#e879f9', '#22d3ee']),
        [colorKey]
    );

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const ctx = canvas?.getContext('2d');

        if (!canvas || !container || !ctx) {
            return undefined;
        }

        let width = 0;
        let height = 0;
        let noiseTime = 0;

        const resize = () => {
            const bounds = container.getBoundingClientRect();
            const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

            width = Math.max(1, Math.floor(bounds.width));
            height = Math.max(1, Math.floor(bounds.height));

            canvas.width = width * pixelRatio;
            canvas.height = height * pixelRatio;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
            ctx.filter = `blur(${blur}px)`;
        };

        const drawWave = () => {
            noiseTime += speedValue;

            for (let waveIndex = 0; waveIndex < waveCount; waveIndex += 1) {
                ctx.beginPath();
                ctx.lineWidth = waveWidth || 50;
                ctx.strokeStyle = waveColors[waveIndex % waveColors.length];

                for (let x = 0; x < width; x += 5) {
                    const y = noise.current(x / 800, 0.3 * waveIndex, noiseTime) * waveAmplitude;
                    ctx.lineTo(x, y + height * waveY);
                }

                ctx.stroke();
                ctx.closePath();
            }
        };

        const render = () => {
            ctx.globalAlpha = 1;
            ctx.clearRect(0, 0, width, height);

            if (backgroundFill && backgroundFill !== 'transparent') {
                ctx.fillStyle = backgroundFill;
                ctx.fillRect(0, 0, width, height);
            }

            ctx.globalAlpha = waveOpacity || 0.5;
            drawWave();
            animationFrameRef.current = requestAnimationFrame(render);
        };

        resize();
        render();
        window.addEventListener('resize', resize);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            window.removeEventListener('resize', resize);
        };
    }, [backgroundFill, blur, speedValue, waveOpacity, waveWidth, waveCount, waveAmplitude, waveY, waveColors]);

    const [isSafari, setIsSafari] = useState(false);
    useEffect(() => {
        setIsSafari(
            typeof window !== 'undefined' &&
                navigator.userAgent.includes('Safari') &&
                !navigator.userAgent.includes('Chrome')
        );
    }, []);

    return (
        <div
            ref={containerRef}
            className={cn('h-screen flex flex-col items-center justify-center', containerClassName)}
        >
            <canvas
                className="absolute inset-0 z-0"
                ref={canvasRef}
                id="canvas"
                style={{
                    ...(isSafari ? { filter: `blur(${blur}px)` } : {}),
                }}
            ></canvas>
            <div
                className={cn('relative z-10', className)}
                {...props}
            >
                {children}
            </div>
        </div>
    );
};
